import { useContext, useEffect, useState, type ComponentProps } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Typography, { TypographyProps } from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import PrintIcon from '@mui/icons-material/Print';
import TextField from '@mui/material/TextField';

import {
  formatDate,
  formatCurrency,
} from '#common/utils';
import { useGetOrderMutation } from '#services/order';
import EnhancedTable, { EnhancedTableProps } from '#components/EnhancedTable';
import UserShippingInfo from '#components/UserShippingInfo';
import UserAdditionalChanges from '#components/UserAdditionalChanges';
import UserInvoices from '#components/UserInvoices';
import UserTransactions from '#components/UserTransactions';
import UserOrderItems from '#components/UserOrderItems';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import {
  FULFILLMENT_STATUS,
} from '#common/constants';

interface OrderUser {
  disableShippingInfo?: boolean;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  shippingName?: string;
  shippingEmail?: string;
  shippingPhone?: string;
  shippingAddress?: string;
  disableBillingInfo?: boolean;
  billingName?: string;
  billingEmail?: string;
  billingPhone?: string;
  billingAddress?: string;
  [key: string]: unknown;
}

interface Order {
  id?: string | number;
  items: Array<Record<string, unknown>>;
  cancelledItems?: Array<Record<string, unknown>>;
  additionalCharges: Array<Record<string, unknown>>;
  invoices?: Array<Record<string, unknown>>;
  transactions?: Array<{ amount?: number | string; [key: string]: unknown }>;
  packages?: Array<Record<string, unknown>>;
  paymentInfo?: Array<{ amount?: number | string; [key: string]: unknown }>;
  createdDate?: string;
  creater: { name?: string; [key: string]: unknown };
  user: OrderUser;
  couponSnapshots?: Array<{ code?: string; name?: string }>;
  couponDiscount?: number;
  notesForAll?: string;
  fulfillmentStatus?: string | number;
  [key: string]: unknown;
}

export default function UserOrderDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('order');
  const [order, setOrder] = useState<Order | undefined>();
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (meta) {
      meta.setAttribute('content', 'width=device-width, initial-scale=1.0');
    } else {
      const newMeta = document.createElement('meta');
      newMeta.name = 'viewport';
      newMeta.content = 'width=device-width, initial-scale=1.0';
      document.head.appendChild(newMeta);
    }

    return () => {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.remove();
      }
    };
  }, []);

  const [getOrder] = useGetOrderMutation();

  const {
    hideBackdrop,
  } = useContext(FeedbackUIContext)!;

  useEffect(() => {
    const match = location.search.match(/orderId=(\S+)/i);
    if (!match) {
      return;
    }

    hideBackdrop();
    void getOrder({ orderId: match[1] })
      .then((res) => {
        const result = res as { error?: unknown; data?: Order };
        hideBackdrop();
        if (result.error) {
          return;
        }
        setOrder(result.data);
        setCancelled(result.data?.fulfillmentStatus === FULFILLMENT_STATUS.CANCELED);
      });
  }, []);

  if (!order) {
    return null;
  }

  const standardItems = order.items.filter((item) => !item.shipDatePending);
  const pendingItems = order.items.filter((item) => item.shipDatePending);
  const cancelledItems = order.cancelledItems ?? [];
  // headings only help when the order is split into more than one group
  const showGroupTitles = [standardItems, pendingItems, cancelledItems]
    .filter((rows) => rows.length).length > 1;
  const itemGroups = [
    { key: 'standard', rows: standardItems },
    { key: 'pending', rows: pendingItems, pending: true },
    { key: 'cancelled', rows: cancelledItems, cancelled: true },
  ].filter((group) => group.rows.length);

  const additionalChargesProps = {
    rows: order.additionalCharges,
  };

  const invoicesProps = {
    rows: order.invoices || [],
  };

  const transactionsProps = {
    rows: order.transactions || [],
  };

  const shippingInfoProps = {
    rows: order.packages || [],
  };

  const paymentInfoProps = {
    // WORKAROUND: paymentInfo is a new added field
    // use fallback logic to prevent touching the database
    rows: order.paymentInfo || [],
  };

  let subtotal = order.items.reduce((total, item) => total + Number(item.totalPrice), 0);

  subtotal = order.additionalCharges.reduce(
    (total, additional) => total + Number(additional.price),
    subtotal,
  );

  if (cancelled) {
    subtotal = 0;
  }

  const paymentHeaders: EnhancedTableProps['headers'] = [{
    id: 'transactionId',
    label: t('paymentTable.transactionId'),
  },
  {
    id: 'description',
    label: t('paymentTable.description'),
    sx: {
      maxWidth: 400,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
  },
  {
    id: 'paymentDate',
    label: t('paymentTable.paymentDate'),
    type: 'date',
    renderer: (row) => formatDate(row.paymentDate as Parameters<typeof formatDate>[0]),
  },
  {
    id: 'amount',
    label: t('paymentTable.amount'),
    type: 'number',
    defaultValue: '',
    renderer: (row) => formatCurrency(row.amount as Parameters<typeof formatCurrency>[0]),
  }];

  return (
    <Grid container sx={{ px: { xs: 2, sm: 0 } }}>
      <Grid
        sx={{
          py: { xs: 2, sm: 3 },
          displayPrint: 'none',
          display: 'flex',
          alignItems: { xs: 'center', sm: 'flex-start' },
          gap: { xs: 2, sm: 0 },
          flexDirection: { xs: 'row', sm: 'column' },
        }}
        size={{
          xs: 12,
          sm: 2,
        }}
      >
        <Box>
          <Typography variant={'h7' as TypographyProps['variant']} gutterBottom>
            {t('detail.title')}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="text"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
          >
            {t('detail.print')}
          </Button>
        </Box>
      </Grid>
      <Grid
        sx={{
          pt: { xs: 0, sm: 5 },
          pl: { xs: 0, sm: 2 },
        }}
        size={{
          xs: 12,
          sm: 10,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            px: { xs: 2, sm: 5 },
            pt: { xs: 3, sm: 5 },
            pb: 2,
            mb: 5,
            '& .MuiGrid-root': { mb: 2 },
          }}
        >
          <Box sx={{ typography: 'subtitle2' }}>
            <Grid container>
              <Grid size={4}>
                {t('detail.orderNumber')}
              </Grid>
              <Grid size={8}>
                {order.id}
              </Grid>

              <Grid size={4}>
                {t('detail.orderDate')}
              </Grid>
              <Grid size={8}>
                {formatDate(order.createdDate as string)}
              </Grid>

              <Grid size={4}>
                {t('detail.placedBy')}
              </Grid>
              <Grid size={8}>
                {order.creater.name}
              </Grid>

              <Grid size={4}>
                {t('detail.shippingInfo')}
              </Grid>
              <Grid size={8}>
                <Box>
                  {
                    order.user.disableShippingInfo
                      ? order.user.name
                      : order.user.shippingName
                  }
                </Box>
                <Box>
                  {
                    order.user.disableShippingInfo
                      ? order.user.email
                      : order.user.shippingEmail
                  }
                </Box>
                <Box>
                  {
                    order.user.disableShippingInfo
                      ? order.user.phone
                      : order.user.shippingPhone
                  }
                </Box>
                <Box>
                  {
                    order.user.disableShippingInfo
                      ? order.user.address
                      : order.user.shippingAddress
                  }
                </Box>
              </Grid>

              <Grid size={4}>
                {t('detail.billingInfo')}
              </Grid>
              <Grid size={8}>
                <Box>
                  {
                    order.user.disableBillingInfo
                      ? order.user.name
                      : order.user.billingName
                  }
                </Box>
                <Box>
                  {
                    order.user.disableBillingInfo
                      ? order.user.email
                      : order.user.billingEmail
                  }
                </Box>
                <Box>
                  {
                    order.user.disableBillingInfo
                      ? order.user.phone
                      : order.user.billingPhone
                  }
                </Box>
                <Box>
                  {
                    order.user.disableBillingInfo
                      ? order.user.address
                      : order.user.billingAddress
                  }
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {itemGroups.map((group) => (
          <Box key={group.key}>
            {showGroupTitles && (
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                {t(`itemGroups.${group.key}`)}
              </Typography>
            )}
            <UserOrderItems
              orderItems={{ rows: group.rows }}
              order={order as unknown as Parameters<typeof UserOrderItems>[0]['order']}
              cancelled={cancelled || !!group.cancelled}
              pending={!!group.pending}
            />
          </Box>
        ))}

        <UserAdditionalChanges
          additionalCharges={additionalChargesProps}
        />

        <Box
          sx={{
            typography: 'subtitle1',
            display: 'flex',
            justifyContent: 'flex-end',
            pt: 2,
          }}
        >
          <b>{t('detail.subtotal')}</b>
          :
          {' '}
          {formatCurrency(subtotal)}
        </Box>

        {/* Display coupon discounts if any */}
        {order.couponSnapshots && order.couponSnapshots.length > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: 'success.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'success.main',
            }}
          >
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                color: 'success.dark',
              }}
            >
              {t('detail.couponsApplied')}
            </Typography>
            {order.couponSnapshots.map((coupon, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  typography: 'body2',
                  color: 'success.dark',
                  mb: 0.5,
                }}
              >
                <span>
                  <b>{coupon.code}</b>
                  {' - '}
                  {coupon.name}
                </span>
              </Box>
            ))}
            {order.couponDiscount && order.couponDiscount > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  typography: 'subtitle2',
                  color: 'success.main',
                  mt: 1,
                  pt: 1,
                  borderTop: '1px solid',
                  borderColor: 'success.light',
                }}
              >
                <b>{t('detail.totalDiscount')}</b>
                {' '}
                <span>
                  -
                  {formatCurrency(order.couponDiscount)}
                </span>
              </Box>
            )}
          </Box>
        )}

        {/* Final Order Total */}
        <Box
          sx={{
            typography: 'h6',
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 2,
            pt: 2,
            borderTop: '2px solid',
            borderColor: 'divider',
          }}
        >
          <b>{t('detail.orderTotal')}</b>
          :
          {' '}
          {formatCurrency(order.couponDiscount ? subtotal - order.couponDiscount : subtotal)}
        </Box>

        <Divider />

        <Box sx={{ mt: 5, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('detail.shippingInfo')}
          </Typography>
        </Box>

        <UserShippingInfo
          shippingInfo={shippingInfoProps}
        />

        <Box sx={{ mt: 5, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('detail.transactions')}
          </Typography>
        </Box>

        <UserTransactions
          transactions={transactionsProps}
          order={order}
        />

        <Typography variant="subtitle2" sx={{ mt: 2, textAlign: 'right' }}>
          {t('detail.totalPaid', { amount: formatCurrency((order.transactions || []).reduce((acc, trans) => acc + Number(trans.amount || 0), 0)) })}
        </Typography>

        <Box sx={{ mt: 5, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('detail.invoices')}
          </Typography>
        </Box>

        <UserInvoices
          invoices={invoicesProps}
          order={order as unknown as ComponentProps<typeof UserInvoices>['order']}
        />

        <Box sx={{ mt: 5, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('detail.paymentInfo')}
          </Typography>
        </Box>

        <Box sx={{ mb: 5 }}>
          <EnhancedTable
            name="paymentInfo"
            headers={paymentHeaders}
            {...paymentInfoProps}
          />
          <Typography variant="subtitle2" sx={{ mt: 2, textAlign: 'right' }}>
            {t('detail.remainingBalance', { amount: formatCurrency(subtotal - (order.paymentInfo || []).reduce((acc, curr) => acc + Number(curr.amount), 0)) })}
          </Typography>
        </Box>

        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            mt: 5,
          }}
        >
          <Typography variant="subtitle2">
            {t('detail.notesForCustomer')}
          </Typography>
          <TextField
            multiline
            fullWidth
            rows={5}
            value={order.notesForAll}
          />
        </Box>

        <Button
          sx={{
            displayPrint: 'none',
            mt: 5,
            mb: 2,
          }}
          variant="contained"
          onClick={() => { void navigate('/order'); }}
        >
          {t('detail.backToList')}
        </Button>

      </Grid>
    </Grid>
  );
}
